/**
 * Timeline Data Hook
 * 
 * Custom hook for managing timeline events data including loading,
 * error handling, and pagination.
 */

import { useState, useEffect, useCallback } from 'react';
import { WorkOrderEvent, DateRange } from '../services/equipment/types';
import { eventsService } from '../services/equipment/events-service';
import { useDateRange } from './use-date-range';

interface UseTimelineDataOptions {
  site?: string;
  /** Equipment ID to fetch events for */
  equipmentId?: string;
  /** Initial limit for events */
  initialLimit?: number;
  /** Auto-fetch on mount */
  autoFetch?: boolean;
  /** Whether to use date range filtering from context */
  useDateRangeFiltering?: boolean;
}

interface UseTimelineDataReturn {
  /** Array of work order events */
  events: WorkOrderEvent[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Whether there are more events to load */
  hasMore: boolean;
  /** Current limit */
  limit: number;
  /** Current date range being used for filtering */
  dateRange: DateRange | null;
  /** Fetch events function */
  fetchEvents: (site: string, equipmentId: string, limit?: number, dateRange?: DateRange) => Promise<void>;
  /** Load more events function */
  loadMore: () => Promise<void>;
  /** Refresh events function */
  refresh: () => Promise<void>;
  /** Clear events and reset state */
  clear: () => void;
  /** Set limit function */
  setLimit: (limit: number) => void;
}

/**
 * Hook for managing timeline events data
 */
export const useTimelineData = (options: UseTimelineDataOptions = {}): UseTimelineDataReturn => {
  const {
    site,
    equipmentId,
    initialLimit = 10,
    autoFetch = true,
    useDateRangeFiltering = true
  } = options;

  // Get date range from context if enabled
  const { dateRange: contextDateRange } = useDateRangeFiltering ? useDateRange() : { dateRange: null };

  const [events, setEvents] = useState<WorkOrderEvent[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [limit, setLimit] = useState(initialLimit);
  const [currentSite, setCurrentSite] = useState<string | undefined>(site);
  const [currentEquipmentId, setCurrentEquipmentId] = useState<string | undefined>(equipmentId);
  const [currentDateRange, setCurrentDateRange] = useState<DateRange | null>(contextDateRange);

  /**
   * Fetch events for a specific equipment ID
   */
  const fetchEvents = useCallback(async (
    targetSite: string,
    targetEquipmentId: string,
    targetLimit?: number,
    targetDateRange?: DateRange
  ) => {
    if (!targetSite || !targetEquipmentId) return;

    setIsLoading(true);
    setError(null);

    try {
      const fetchLimit = targetLimit || limit;
      const dateRangeToUse = targetDateRange || (useDateRangeFiltering ? contextDateRange : null);

      const fetchedEvents = await eventsService.getWorkOrderEvents(
        targetSite,
        targetEquipmentId,
        dateRangeToUse || undefined,
        fetchLimit + 1
      );

      // Check if there are more events by fetching one extra
      const hasMoreEvents = fetchedEvents.length > fetchLimit;
      const eventsToShow = hasMoreEvents ? fetchedEvents.slice(0, fetchLimit) : fetchedEvents;

      setEvents(eventsToShow);
      setHasMore(hasMoreEvents);
      setCurrentSite(targetSite);
      setCurrentEquipmentId(targetEquipmentId);
      setCurrentDateRange(dateRangeToUse);

      if (targetLimit) {
        setLimit(targetLimit);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch timeline events';
      setError(errorMessage);
      setEvents([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, [limit, contextDateRange, useDateRangeFiltering]);

  /**
   * Load more events (increase limit and refetch)
   */
  const loadMore = useCallback(async () => {
    if (! currentSite || !currentEquipmentId || !hasMore || isLoading) return;

    const newLimit = limit + initialLimit;
    await fetchEvents(currentSite, currentEquipmentId, newLimit, currentDateRange || undefined);
  }, [currentEquipmentId, hasMore, isLoading, limit, initialLimit, currentDateRange, fetchEvents]);

  /**
   * Refresh current events
   */
  const refresh = useCallback(async () => {
    if (!currentSite || !currentEquipmentId) return;
    await fetchEvents(currentSite, currentEquipmentId, limit, currentDateRange || undefined);
  }, [currentSite, currentEquipmentId, limit, currentDateRange, fetchEvents]);

  /**
   * Clear events and reset state
   */
  const clear = useCallback(() => {
    setEvents([]);
    setError(null);
    setHasMore(true);
    setLimit(initialLimit);
    setCurrentSite(undefined);
    setCurrentEquipmentId(undefined);
    setCurrentDateRange(null);
  }, [initialLimit]);

  /**
   * Update limit and refetch if needed
   */
  const updateLimit = useCallback((newLimit: number) => {
    setLimit(newLimit);
    if (currentSite && currentEquipmentId && newLimit !== limit) {
      fetchEvents(currentSite, currentEquipmentId, newLimit, currentDateRange || undefined);
    }
  }, [currentEquipmentId, limit, currentDateRange, fetchEvents]);

  // Auto-fetch when equipmentId changes
  useEffect(() => {
    if (autoFetch && site && equipmentId && (site !== currentSite || equipmentId !== currentEquipmentId)) {
      fetchEvents(site, equipmentId);
    }
  }, [site, currentSite, equipmentId, currentEquipmentId, autoFetch, fetchEvents]);

  // Clear events when equipmentId is removed
  useEffect(() => {
    if ((!site && currentSite) || (!equipmentId && currentEquipmentId)) {
      clear();
    }
  }, [site, currentSite, equipmentId, currentEquipmentId, clear]);

  // Refetch events when date range changes (if using date range filtering)
  useEffect(() => {
    if (
      useDateRangeFiltering &&
      contextDateRange &&
      currentSite &&
      currentEquipmentId &&
      contextDateRange !== currentDateRange
    ) {
      fetchEvents(currentSite, currentEquipmentId, limit, contextDateRange);
    }
  }, [contextDateRange, currentDateRange, currentSite, currentEquipmentId, limit, useDateRangeFiltering, fetchEvents]);

  return {
    events,
    isLoading,
    error,
    hasMore,
    limit,
    dateRange: currentDateRange,
    fetchEvents,
    loadMore,
    refresh,
    clear,
    setLimit: updateLimit
  };
};